

/**
 * Query timeout duration in milliseconds
 */
const DEFAULT_TIMEOUT = 10000; // 10 seconds

/**
 * Wraps a Supabase query with timeout protection
 * @param queryPromise - The Supabase query promise
 * @param timeoutMs - Timeout in milliseconds (default: 10000)
 * @returns Promise that resolves with query result or rejects on timeout
 */
export async function queryWithTimeout<T>(
    queryPromise: Promise<T>,
    timeoutMs: number = DEFAULT_TIMEOUT
): Promise<T> {
    return Promise.race([
        queryPromise,
        new Promise<T>((_, reject) =>
            setTimeout(
                () => reject(new Error(`Query timeout after ${timeoutMs}ms`)),
                timeoutMs
            )
        ),
    ]);
}

/**
 * Retry options for database queries
 */
interface RetryOptions {
    maxRetries?: number;
    initialDelay?: number;
    maxDelay?: number;
    backoffMultiplier?: number;
}

/**
 * Retries a query with exponential backoff
 * @param queryFn - Function that returns a query promise
 * @param options - Retry configuration
 * @returns Promise that resolves with query result
 */
export async function retryQuery<T>(
    queryFn: () => Promise<T>,
    options: RetryOptions = {}
): Promise<T> {
    const {
        maxRetries = 3,
        initialDelay = 1000,
        maxDelay = 5000,
        backoffMultiplier = 2,
    } = options;

    let lastError: Error | null = null;
    let delay = initialDelay;

    for (let attempt = 0; attempt < maxRetries; attempt++) {
        try {
            return await queryFn();
        } catch (error) {
            lastError = error as Error;
            console.warn(`Query attempt ${attempt + 1} failed:`, error);

            if (attempt < maxRetries - 1) {
                // Wait before retrying (exponential backoff)
                await new Promise((resolve) => setTimeout(resolve, delay));
                delay = Math.min(delay * backoffMultiplier, maxDelay);
            }
        }
    }

    throw new Error(
        `Query failed after ${maxRetries} attempts. Last error: ${lastError?.message}`
    );
}

/**
 * Error types for better error handling
 */
export enum SupabaseErrorType {
    TIMEOUT = 'TIMEOUT',
    PERMISSION_DENIED = 'PERMISSION_DENIED',
    NETWORK = 'NETWORK',
    RLS_POLICY = 'RLS_POLICY',
    NOT_FOUND = 'NOT_FOUND',
    UNKNOWN = 'UNKNOWN',
}

/**
 * Analyzes Supabase error and returns user-friendly message
 * @param error - The error object
 * @returns Object with error type and user-friendly message
 */
export function handleSupabaseError(error: any): {
    type: SupabaseErrorType;
    message: string;
    details?: string;
} {
    const errorMessage = error?.message || String(error);
    const errorCode = error?.code;

    // Timeout errors
    if (errorMessage.includes('timeout') || errorMessage.includes('Timeout')) {
        return {
            type: SupabaseErrorType.TIMEOUT,
            message: 'Kết nối cơ sở dữ liệu quá chậm. Vui lòng thử lại.',
            details: errorMessage,
        };
    }

    // Permission/RLS errors
    if (
        errorMessage.includes('permission denied') ||
        errorMessage.includes('RLS') ||
        errorMessage.includes('policy') ||
        errorCode === '42501'
    ) {
        return {
            type: SupabaseErrorType.PERMISSION_DENIED,
            message: 'Bạn không có quyền truy cập dữ liệu này.',
            details: errorMessage,
        };
    }

    // Network errors
    if (
        errorMessage.includes('network') ||
        errorMessage.includes('fetch') ||
        errorMessage.includes('NetworkError')
    ) {
        return {
            type: SupabaseErrorType.NETWORK,
            message: 'Lỗi kết nối mạng. Vui lòng kiểm tra internet và thử lại.',
            details: errorMessage,
        };
    }

    // Not found errors
    if (errorMessage.includes('not found') || errorCode === '42P01') {
        return {
            type: SupabaseErrorType.NOT_FOUND,
            message: 'Không tìm thấy dữ liệu yêu cầu.',
            details: errorMessage,
        };
    }

    // Unknown errors
    return {
        type: SupabaseErrorType.UNKNOWN,
        message: 'Đã có lỗi xảy ra. Vui lòng thử lại hoặc liên hệ quản trị viên.',
        details: errorMessage,
    };
}

/**
 * Safe query wrapper with timeout, retry, and error handling
 * @param queryFn - Function that returns a Supabase query promise
 * @param options - Configuration options
 * @returns Promise with query result or null on error
 */
export async function safeQuery<T>(
    queryFn: () => Promise<{ data: T | null; error: any }>,
    options: {
        timeout?: number;
        retry?: boolean;
        retryOptions?: RetryOptions;
        onError?: (error: ReturnType<typeof handleSupabaseError>) => void;
    } = {}
): Promise<{ data: T | null; error: any }> {
    const { timeout = DEFAULT_TIMEOUT, retry = true, retryOptions, onError } = options;

    try {
        const queryWithRetry = retry
            ? () => retryQuery(queryFn, retryOptions)
            : queryFn;

        const result = await queryWithTimeout(queryWithRetry(), timeout);

        // Check for Supabase-specific errors
        if (result.error) {
            const handledError = handleSupabaseError(result.error);
            console.error('Supabase query error:', handledError);

            if (onError) {
                onError(handledError);
            }

            return { data: null, error: result.error };
        }

        return result;
    } catch (error) {
        const handledError = handleSupabaseError(error);
        console.error('Query failed:', handledError);

        if (onError) {
            onError(handledError);
        }

        return { data: null, error };
    }
}

/**
 * Example usage:
 * 
 * const { data, error } = await safeQuery(
 *   () => supabase.from('products').select('*'),
 *   {
 *     timeout: 5000,
 *     retry: true,
 *     onError: (err) => {
 *       toast.error(err.message);
 *     }
 *   }
 * );
 */
